import { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';

import BookButton from 'components/Button/BookButton';

import useInView from 'hooks/useInView';
import useMouseMaskAnimation from 'hooks/useMouseMaskAnimation';
import useAnimateTextPath from 'hooks/useAnimateTextPath';

import BREAKPOINTS from 'styles/breakpoints';
import { CircleMask } from 'src/styles/global';
import COLORS from 'styles/colors';
import FONTS from 'styles/fonts';
import { rem, weight } from 'styles/typography';

import { isMobileDevice, isTouchDevice } from 'utils/helpers';

import TextSvg from 'public/images/text/booking-cta.svg';
import TextOutlineSvg from 'public/images/text/booking-cta-outline.svg';

const Section = styled.section`
  background-color: ${(props) => props.bgColor || COLORS.sky};
`;

const Content = styled.div`
  position: relative;
  min-height: 440px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${BREAKPOINTS.small`
    min-height: 700px;
  `}

  ${BREAKPOINTS.large`
    min-height: 860px;
  `}
`;

const BgImage = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: inherit;
  background: url('/images/backgrounds/bg-booking-cta.jpg') no-repeat center;
  background-size: cover;
  border-radius: 50vw 50vw 0 0;
  overflow: hidden;

  mask: url('/images/masks/circle.svg') no-repeat;
  mask-size: 400px 400px;
  -webkit-mask: url('/images/masks/circle.svg') no-repeat;
  -webkit-mask-size: 400px 400px;

  ${BREAKPOINTS.medium`
    display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};
  `}
`;

const TextStyles = css`
  position: absolute;
  top: 17px;
  left: 0;

  ${BREAKPOINTS.custom(600)`
    top: 0;
  `}

  text {
    font-family: ${FONTS.wulkan};
    font-weight: ${weight.black};
    font-size: calc(102px - 4vw);

    ${BREAKPOINTS.small`
      font-size: ${rem(65)};
    `}

    ${BREAKPOINTS.medium`
      font-size: ${rem(50)};
    `}
  }
`;

const ReadyToTakeAShot = styled(TextSvg)`
  ${TextStyles}
`;

const ReadyToTakeAShotOutline = styled(TextOutlineSvg)`
  ${TextStyles}
`;

const BookButtonStyled = styled(BookButton)`
  margin-top: 45px;

  ${BREAKPOINTS.medium`
    margin-top: 57px;
  `}
`;

const BookingCTA = ({
  bgColor = '',
  ctaLabel = '',
  ctaLink = '',
  ...props
}) => {
  const circleRef = useRef(null);
  const [inViewRef, inView] = useInView(false);
  const [scrollOffset, setScrollOffset] = useState(0);

  useEffect(() => {
    const threshold = 600;
    const { top } = inViewRef?.current?.getBoundingClientRect();
    const { scrollTop } = document.documentElement;

    setScrollOffset(top + scrollTop - threshold);
  }, [inViewRef]);

  useMouseMaskAnimation(circleRef, inView, isTouchDevice);

  useAnimateTextPath(scrollOffset, isMobileDevice, inView);

  return (
    <Section bgColor={bgColor} ref={inViewRef} {...props}>
      <Content>
        <CircleMask bgColor={COLORS.forest}>
          <ReadyToTakeAShot />
        </CircleMask>

        <BgImage ref={circleRef} isHidden={isTouchDevice()}>
          <ReadyToTakeAShotOutline />
        </BgImage>

        <BookButtonStyled size="large" label={ctaLabel} href={ctaLink} />
      </Content>
    </Section>
  );
};

export default BookingCTA;
